import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'

import { useAgentVerificationDecisionContext } from '../../../useAgentVerificationDecisionContext/useAgentVerificationDecisionContext'

import { Modal, ActionModal, Alert, AcknowledgeModal, FormField, Select, MultiSelect } from '@percent/lemonade'
import {
  agentVerificationApprovalCodes,
  AgentVerificationUpdateStatusModalProps
} from './AgentVerificationUpdateStatusModal.types'
import {
  AgentVerificationApprovalReasonDropdownCodes,
  AgentVerificationRejectionReasonDropdownCodes,
  AgentVerificationStatus,
  FraudulentFlag,
  PartnerName
} from '@percent/admin-dashboard/api/types'
import { getRejectionReasonCodes } from '@percent/admin-dashboard/common/utility/getRejectionReasonCodes'
import styles from './AgentVerificationUpdateStatusModal.module.scss'
import { useMutation, useRequiredParams } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { ErrorView } from '@percent/admin-dashboard/common/components'
import { LocaleKey } from '@percent/admin-dashboard/i18n/config'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'

export function AgentVerificationUpdateStatusModal({
  agentFullName: agentName,
  organisationName,
  agentVerificationStatus,
  refresh,
  allowedOrganisations,
  partnerName
}: AgentVerificationUpdateStatusModalProps) {
  const { id } = useRequiredParams()
  const { t } = useTranslation()
  const { setOpenedModal, isUpdateStatusModalOpened } = useAgentVerificationDecisionContext()
  const { agentVerificationService } = useServices()
  const agentVerificationManualFradulentRejectionFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.AD_AGENT_VERIFICATION_MANUAL_FRAUDLENT_REJECTION
  )
  const [isSuspiciousApplication, setIsSuspiciousApplication] = useState(false)
  const [fraudulentFlags, setFraudulentFlags] = useState<FraudulentFlag[]>([])

  const [isStatusUpdated, setIsStatusUpdated] = useState(false)

  const isInApprovedState = agentVerificationStatus === AgentVerificationStatus.APPROVED
  const isGooglePartner = partnerName?.toLowerCase() === PartnerName.GOOGLE
  const isMicrosoftPartner = partnerName?.toLowerCase() === PartnerName.MICROSOFT

  const onSuccess = () => {
    setIsStatusUpdated(true)
  }

  const [
    { isLoading: isRejectAgentVerificationLoading, errorMessage: rejectAgentVerificationErrorMessage },
    { apiFunc: rejectAgentVerification }
  ] = useMutation(agentVerificationService.rejectAgentVerification, onSuccess)

  const [
    { isLoading: isApproveAgentVerificationLoading, errorMessage: approveAgentVerificationErrorMessage },
    { apiFunc: approveAgentVerification }
  ] = useMutation(agentVerificationService.approveAgentVerification, onSuccess)

  const getFraudulentFlags = useCallback(async () => {
    try {
      const { data: fraudulentFlagsData } = await agentVerificationService.getFraudulentFlags()
      setFraudulentFlags(fraudulentFlagsData.data)
    } catch {
      setFraudulentFlags([])
    }
  }, [agentVerificationService])

  const { values, setFieldValue, submitForm } = useFormik<{
    updateReasonCode:
      | AgentVerificationRejectionReasonDropdownCodes
      | AgentVerificationApprovalReasonDropdownCodes
      | undefined
    fraudulentRejectionReasons: string[]
  }>({
    initialValues: {
      updateReasonCode: undefined,
      fraudulentRejectionReasons: []
    },
    validationSchema: () =>
      yup.object().shape({
        updateReasonCode: yup.string().required('Required')
      }),
    onSubmit: submittedValue => {
      if (isInApprovedState) {
        const rejection = {
          id,
          rejectionReasonCode: submittedValue.updateReasonCode as AgentVerificationRejectionReasonDropdownCodes
        }

        rejectAgentVerification(
          agentVerificationManualFradulentRejectionFeatureFlag
            ? { ...rejection, fraudulentFlags: submittedValue.fraudulentRejectionReasons }
            : rejection
        )
      } else {
        approveAgentVerification({
          id,
          approvalReasonCode: submittedValue.updateReasonCode as AgentVerificationApprovalReasonDropdownCodes
        })
      }
    }
  })

  const closeModal = () => setOpenedModal(undefined)

  const handleCloseModalAfterStatusUpdate = () => {
    refresh()
    closeModal()
    setIsStatusUpdated(false)
  }

  const getOptionLabel = useCallback(
    (reason: AgentVerificationRejectionReasonDropdownCodes | AgentVerificationApprovalReasonDropdownCodes) => {
      return t(
        isInApprovedState ? (`dropdown.reject.${reason}` as LocaleKey) : (`dropdown.approve.${reason}` as LocaleKey)
      )
    },
    [t, isInApprovedState]
  )

  const updateReasonsOptions = useMemo(
    () =>
      (isInApprovedState
        ? getRejectionReasonCodes(allowedOrganisations, isGooglePartner || isMicrosoftPartner).filter(
            val =>
              agentVerificationManualFradulentRejectionFeatureFlag ||
              val !== AgentVerificationRejectionReasonDropdownCodes.SuspiciousApplication
          )
        : agentVerificationApprovalCodes
      ).map(reason => ({
        label: getOptionLabel(reason),
        value: reason
      })),
    [
      isInApprovedState,
      allowedOrganisations,
      isGooglePartner,
      isMicrosoftPartner,
      agentVerificationManualFradulentRejectionFeatureFlag,
      getOptionLabel
    ]
  )
  const errorMessage = rejectAgentVerificationErrorMessage || approveAgentVerificationErrorMessage

  const fraudulentReasonsOptions = useMemo(() => {
    const predefinedScopes = ['organisation_website', 'organisation_email', 'agent']

    return predefinedScopes.map(scope => ({
      title: t(`fraudReason.scope.${scope}` as LocaleKey),
      options: fraudulentFlags
        .filter(flag => flag.code.startsWith(scope))
        .map(flag => ({ label: t(`fraudReason.label.${flag.code}`), value: flag.code }))
    }))
  }, [t, fraudulentFlags])

  useEffect(() => {
    if (isUpdateStatusModalOpened && agentVerificationManualFradulentRejectionFeatureFlag) {
      getFraudulentFlags()
    }
  }, [isUpdateStatusModalOpened, agentVerificationManualFradulentRejectionFeatureFlag, getFraudulentFlags])

  useEffect(() => {
    if (agentVerificationManualFradulentRejectionFeatureFlag) {
      setIsSuspiciousApplication(
        values.updateReasonCode === AgentVerificationRejectionReasonDropdownCodes.SuspiciousApplication
      )
    }
  }, [values.updateReasonCode, agentVerificationManualFradulentRejectionFeatureFlag])

  return (
    <Modal open={isUpdateStatusModalOpened} onClose={closeModal}>
      {isStatusUpdated && (
        <AcknowledgeModal
          title={t(
            isInApprovedState
              ? `dialog.updateAvStatus.requestIsRejected.title`
              : 'dialog.updateAvStatus.requestIsApproved.title'
          )}
          description={t(
            isInApprovedState
              ? `dialog.updateAvStatus.requestIsRejected.description`
              : 'dialog.updateAvStatus.requestIsApproved.description',
            { agentName, organisationName }
          )}
          handleClose={handleCloseModalAfterStatusUpdate}
          result={isInApprovedState ? 'negative' : 'positive'}
          buttonText={t('button.done')}
        />
      )}

      {!isStatusUpdated && (
        <ActionModal
          title={t('dialog.updateAvStatus.title')}
          primaryButtonText={t('typography.update')}
          primaryBtnTestId="update-av-status"
          secondaryButtonText={t('button.cancel')}
          handleClose={closeModal}
          handleSubmit={submitForm}
          loading={isRejectAgentVerificationLoading || isApproveAgentVerificationLoading}
          disabled={
            !values.updateReasonCode || (isSuspiciousApplication && values.fraudulentRejectionReasons.length === 0)
          }
        >
          <div className={styles.formContainer}>
            <Alert variant="warning">{t('dialog.updateAvStatus.note')}</Alert>
            <span>
              {t(
                isInApprovedState
                  ? 'dialog.updateAvStatus.requestWillBeRejected'
                  : 'dialog.updateAvStatus.requestWillBeApproved',
                { agentName, organisationName }
              )}
            </span>
            <FormField label={t('dialog.updateAvStatus.form.reason.label')} necessity="required">
              <Select
                placeholder={t('dialog.updateAvStatus.form.reason.placeholder')}
                onChange={({ value }) => setFieldValue('updateReasonCode', value)}
                options={updateReasonsOptions}
                defaultValue={
                  values.updateReasonCode && {
                    label: getOptionLabel(values.updateReasonCode),
                    value: values.updateReasonCode
                  }
                }
              />
            </FormField>

            {isSuspiciousApplication && (
              <FormField
                label={t('dropdown.reject.fraudReasonTitle')}
                description={t('dropdown.reject.fraudReasonDescription')}
              >
                <MultiSelect
                  placeholder={t('dropdown.reject.fraudReasonPlaceholder')}
                  options={fraudulentReasonsOptions}
                  onChange={fraudulentReasons =>
                    setFieldValue(
                      'fraudulentRejectionReasons',
                      fraudulentReasons.map(fraudulentReason => fraudulentReason.value)
                    )
                  }
                />
              </FormField>
            )}
            {errorMessage && <ErrorView errorMessage={errorMessage} />}
          </div>
        </ActionModal>
      )}
    </Modal>
  )
}
